import React from "react";
import {
    ChevronUp,
    ChevronDown,
    ChevronsLeft,
    ChevronsRight,
    ChevronLeft,
    ChevronRight,
    ArrowLeft,
} from "lucide-react";
import { Skeleton } from "components/ui/skeleton";
import { useSmoothLoading } from "lib/use-smooth-loading";
import type { RNSCustomerRow } from "../api/index";

interface Props {
    customerName: string;
    onBack: () => void;
    error?: string;
    data: RNSCustomerRow[];
    loading: boolean;
    page: number;
    pageSize: number;
    totalItems: number;
    sortBy: string;
    sortOrder: "asc" | "desc";
    onSortChange: (col: keyof RNSCustomerRow) => void;
    onPageChange: (page: number) => void;
    onPageSizeChange: (size: number) => void;
    hasLoadedOnce: boolean;
}

const STATUS_LABELS: Record<string, string> = {
    delivered: "Delivered",
    in_transit: "In Transit",
    in_stock: "In Stock",
};

const columns: Array<{ key: keyof RNSCustomerRow; label: string; isNumeric?: boolean }> = [
    { key: "cn_number", label: "CN Number" },
    { key: "cn_date", label: "CN Date" },
    { key: "booking_branch", label: "Booking Branch" },
    { key: "consignor", label: "Consignor" },
    { key: "consignee", label: "Consignee" },
    { key: "origin", label: "Origin" },
    { key: "destination", label: "Destination" },
    { key: "status", label: "Status" },
    { key: "rns_days", label: "RNS Days", isNumeric: true },
];

const RNSCustomerTable: React.FC<Props> = ({
    customerName,
    onBack,
    error,
    data,
    loading,
    page,
    pageSize,
    totalItems,
    sortBy,
    sortOrder,
    onSortChange,
    onPageChange,
    onPageSizeChange,
    hasLoadedOnce,
}) => {
    const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));

    const { isLoading } = useSmoothLoading(loading, {
        minLoadingTime: 150,
        debounceTime: 150,
    });

    const renderSkeletonRows = () => {
        return Array.from({ length: 10 }).map((_, i) => (
            <tr key={i}>
                {columns.map((_, j) => (
                    <td key={j} className="border border-gray-200 px-2 py-1.5">
                        <Skeleton className="h-4 w-full" />
                    </td>
                ))}
            </tr>
        ));
    };

    return (
        <div className="flex flex-col h-full gap-6">
            <div className="flex items-center justify-between shrink-0">
                <div className="flex items-center gap-4">
                    <button
                        onClick={onBack}
                        className="p-2 border rounded-md hover:bg-gray-50 text-gray-600 transition-colors"
                        title="Back to customer list"
                    >
                        <ArrowLeft size={16} />
                    </button>
                    <h2 className="text-lg font-bold text-gray-800">{customerName}</h2>
                </div>
            </div>

            {error && (
                <div className="shrink-0 rounded-md border border-red-200 bg-red-50 p-3">
                    <p className="text-sm text-red-600">{error}</p>
                </div>
            )}

            {/* Table Container */}
            <div className="border border-gray-200 overflow-auto flex-1">
                <table className="w-full border-collapse text-sm">
                    <thead>
                        <tr className="bg-gray-100">
                            {columns.map((col) => {
                                const active = sortBy === col.key;

                                return (
                                    <th
                                        key={col.key}
                                        onClick={() => onSortChange(col.key)}
                                        className={`border border-gray-300 px-2 py-2 cursor-pointer whitespace-nowrap font-semibold ${col.isNumeric ? "text-right" : "text-left"}`}
                                    >
                                        <div className={`flex items-center gap-1 ${col.isNumeric ? "justify-end" : "justify-start"}`}>
                                            {col.label}
                                            {active ? (
                                                sortOrder === "asc" ? (<ChevronUp size={14} />) : (<ChevronDown size={14} />)
                                            ) : (<ChevronUp size={14} className="text-gray-300" />)}
                                        </div>
                                    </th>
                                );
                            })}
                        </tr>
                    </thead>

                    <tbody>
                        {isLoading || !hasLoadedOnce ? (
                            renderSkeletonRows()
                        ) : data.length === 0 ? (
                            <tr>
                                <td colSpan={columns.length} className="text-center py-16 text-gray-500">
                                    No data available
                                </td>
                            </tr>
                        ) : (
                            data.map((row, idx) => (
                                <tr key={`${row.cn_number}-${idx}`} className="hover:bg-gray-50 cursor-default">
                                    {columns.map((col) => {
                                        const val = row[col.key];
                                        return (
                                            <td
                                                key={col.key}
                                                className={`border border-gray-200 px-2 py-1.5 whitespace-nowrap ${col.isNumeric ? "text-right" : "text-left"
                                                    }`}
                                            >
                                                {col.key === "cn_date" && val
                                                    ? new Date(val as string).toLocaleDateString("en-IN")
                                                    : col.key === "status"
                                                    ? (val && STATUS_LABELS[val as string]) || val || "--"
                                                    : col.isNumeric
                                                    ? Number(val || 0).toLocaleString("en-IN")
                                                    : val || "--"}
                                            </td>
                                        );
                                    })}
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            <div className="flex items-center justify-between pt-4 shrink-0">
                <span className="text-sm text-gray-600">
                    Showing{" "}
                    <strong>
                        {totalItems === 0 ? 0 : (page - 1) * pageSize + 1}
                    </strong>{" "}
                    to{" "}
                    <strong>{Math.min(page * pageSize, totalItems)}</strong> of{" "}
                    <strong>{totalItems}</strong>
                </span>

                <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2 text-sm">
                        Rows per page
                        <select
                            value={pageSize}
                            onChange={(e) => onPageSizeChange(Number(e.target.value))}
                            className="border rounded px-2 py-1"
                        >
                            {[10, 20, 50, 100].map((s) => (
                                <option key={s} value={s}>
                                    {s}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="flex items-center gap-1">
                        <button
                            disabled={page === 1}
                            onClick={() => onPageChange(1)}
                            className="p-1 border rounded disabled:opacity-50"
                        >
                            <ChevronsLeft size={16} />
                        </button>
                        <button
                            disabled={page === 1}
                            onClick={() => onPageChange(page - 1)}
                            className="p-1 border rounded disabled:opacity-50"
                        >
                            <ChevronLeft size={16} />
                        </button>
                        <span className="text-sm text-gray-700 px-2">
                            Page <strong>{page}</strong> of <strong>{totalPages}</strong>
                        </span>
                        <button
                            disabled={page === totalPages}
                            onClick={() => onPageChange(page + 1)}
                            className="p-1 border rounded disabled:opacity-50"
                        >
                            <ChevronRight size={16} />
                        </button>
                        <button
                            disabled={page === totalPages}
                            onClick={() => onPageChange(totalPages)}
                            className="p-1 border rounded disabled:opacity-50"
                        >
                            <ChevronsRight size={16} />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default RNSCustomerTable;
